import { useEffect, useRef, useState, type ReactNode } from "react";

const MOBILE_QUERY = "(max-width: 767px)";

/**
 * MobileDefer — mobile-only, below-the-fold work shedding.
 *
 * Desktop/tablet (>=768px): renders children exactly as before, with no extra
 * styles, no observers and no state changes — the desktop experience is
 * byte-for-byte identical to not using this wrapper at all.
 *
 * Mobile (<768px): after hydration, any wrapped section that is still far
 * below the viewport is unmounted (its measured height is preserved as a
 * placeholder so scroll position and page height never shift), then remounted
 * via IntersectionObserver when the user scrolls near it. This frees the main
 * thread during initial load (fewer components hydrating, no offscreen
 * timers/carousels running), which improves TBT/TTI, and lets the browser skip
 * layout/paint for the placeholder via `content-visibility`.
 */
export function MobileDefer({
  children,
  minHeight = 400,
}: {
  children: ReactNode;
  minHeight?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [deferred, setDeferred] = useState(false);
  const [placeholder, setPlaceholder] = useState<number | null>(null);

  useEffect(() => {
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) return;
    if (!window.matchMedia(MOBILE_QUERY).matches) return;

    const el = ref.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    // Anything already visible (or about to be) stays mounted — never defer the
    // first screen, so FCP/LCP content is untouched.
    if (rect.top < window.innerHeight * 1.25) return;

    setPlaceholder(Math.max(Math.round(rect.height), minHeight));
    setDeferred(true);

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          io.disconnect();
          setDeferred(false);
        }
      },
      { rootMargin: "700px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [minHeight]);

  return (
    <div
      ref={ref}
      style={
        deferred
          ? {
              minHeight: `${placeholder ?? minHeight}px`,
              contentVisibility: "auto",
              containIntrinsicSize: `${placeholder ?? minHeight}px`,
            }
          : undefined
      }
    >
      {deferred ? null : children}
    </div>
  );
}

export default MobileDefer;
